import React, { useState } from 'react';
import { View, ScrollView, Pressable, ActivityIndicator, Image, StyleSheet } from 'react-native';
import {
  Bevel,
  Button,
  RunningMan,
  Text,
  Window,
  THEME_LIST,
  WALLPAPERS,
  isDesktop,
  makeStyles,
  useTheme,
  useThemeControls,
  type Theme,
} from '@ogaim/ui';
import { canPickImages, pickImage, uploadImage } from '@ogaim/core';
import { useStore } from '../store';

export interface SettingsScreenProps {
  onClose: () => void;
  frame?: boolean;
}

/**
 * Settings. Currently one thing: which era the client dresses up as.
 *
 * The picker previews each theme rather than naming it, because the whole
 * point is how it looks — a list of words would make you switch to find out.
 */
export function SettingsScreen({ onClose, frame = true }: SettingsScreenProps) {
  const styles = useStyles();
  const theme = useTheme();
  const { setThemeId, wallpaper, setWallpaper } = useThemeControls();
  const me = useStore((s) => s.me);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A custom wallpaper rides the same upload path as a photo in a message,
  // so there is one way images get into the system and one set of limits.
  const chooseOwn = async () => {
    setError(null);
    try {
      const picked = await pickImage();
      if (!picked) return;
      setUploading(true);
      const url = await uploadImage(picked);
      setWallpaper('custom', url);
    } catch (err: any) {
      setError(err?.message ?? 'That picture could not be used.');
    } finally {
      setUploading(false);
    }
  };

  const content = (
    <ScrollView contentContainerStyle={styles.body}>
      <Text bold>Appearance</Text>
      <Text disabled style={styles.hint}>
        Applies everywhere, and is remembered on this device.
      </Text>

      <View style={styles.grid}>
        {THEME_LIST.map((t) => (
          <Pressable
            key={t.id}
            onPress={() => setThemeId(t.id)}
            accessibilityRole="radio"
            accessibilityState={{ selected: t.id === theme.id }}
            accessibilityLabel={`${t.label}. ${t.description}`}
            style={styles.cardHit}
          >
            <Bevel
              variant={t.id === theme.id ? 'pressed' : 'raised'}
              style={styles.card}
              contentStyle={styles.cardInner}
            >
              <ThemeSwatch theme={t} />
              <Text bold numberOfLines={1} style={styles.cardLabel}>
                {t.id === theme.id ? '● ' : '○ '}
                {t.label}
              </Text>
              <Text disabled numberOfLines={2} style={styles.cardDesc}>
                {t.description}
              </Text>
            </Bevel>
          </Pressable>
        ))}
      </View>

      <Text bold style={styles.section}>
        Desktop
      </Text>
      <View style={styles.grid}>
        {WALLPAPERS.map((w) => (
          <Pressable
            key={w.id}
            onPress={() => setWallpaper(w.id)}
            accessibilityRole="radio"
            accessibilityState={{ selected: w.id === wallpaper.id }}
            accessibilityLabel={`${w.label}. ${w.description}`}
            style={styles.cardHit}
          >
            <Bevel
              variant={w.id === wallpaper.id ? 'pressed' : 'raised'}
              style={styles.card}
              contentStyle={styles.cardInner}
            >
              <View
                style={[
                  styles.wallSwatch,
                  { backgroundColor: theme.colors.desktop },
                ]}
              >
                {w.uri ? <WallpaperPreview uri={w.uri} /> : null}
              </View>
              <Text bold numberOfLines={1} style={styles.cardLabel}>
                {w.id === wallpaper.id ? '● ' : '○ '}
                {w.label}
              </Text>
            </Bevel>
          </Pressable>
        ))}

        {canPickImages() ? (
          <Pressable
            onPress={chooseOwn}
            accessibilityRole="radio"
            accessibilityState={{ selected: wallpaper.id === 'custom' }}
            accessibilityLabel="Use your own picture as the desktop"
            style={styles.cardHit}
          >
            <Bevel
              variant={wallpaper.id === 'custom' ? 'pressed' : 'raised'}
              style={styles.card}
              contentStyle={styles.cardInner}
            >
              <View style={[styles.wallSwatch, { backgroundColor: theme.colors.face }]}>
                {uploading ? (
                  <ActivityIndicator size="small" />
                ) : wallpaper.id === 'custom' && wallpaper.uri ? (
                  <WallpaperPreview uri={wallpaper.uri} />
                ) : (
                  <Text disabled style={styles.wallPlus}>
                    +
                  </Text>
                )}
              </View>
              <Text bold numberOfLines={1} style={styles.cardLabel}>
                {wallpaper.id === 'custom' ? '● ' : '○ '}
                Your picture…
              </Text>
            </Bevel>
          </Pressable>
        ) : null}
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      {me ? (
        <>
          <Text bold style={styles.section}>
            Screen Name
          </Text>
          <Bevel variant="sunken" background={theme.colors.fieldBg} contentStyle={styles.wellInner}>
            <Text>{me.screenName}</Text>
          </Bevel>
        </>
      ) : null}

      <View style={styles.actions}>
        <Button title="Close" onPress={onClose} primary style={styles.action} />
      </View>
    </ScrollView>
  );

  if (!frame) return <View style={styles.frameless}>{content}</View>;

  return (
    <View style={[styles.backdrop, { backgroundColor: theme.colors.desktop }]}>
      <Window
        title="Settings"
        icon={<RunningMan size={14} />}
        onClose={onClose}
        style={styles.window}
      >
        {content}
      </Window>
    </View>
  );
}

/** Fills its parent with the wallpaper, kept chunky rather than smoothed. */
function WallpaperPreview({ uri }: { uri: string }) {
  return (
    <Image
      source={{ uri }}
      style={StyleSheet.absoluteFill}
      // `contain`, not `cover`: the swatch is much wider than the artwork, so
      // cover cropped it to a band of empty sky and every preview looked
      // identical. Letterboxing shows what you are actually choosing.
      resizeMode="contain"
      fadeDuration={0}
    />
  );
}

/**
 * A miniature window in the given theme: caption bar, face, a field and a
 * button. Enough to tell the three apart at a glance.
 */
function ThemeSwatch({ theme }: { theme: Theme }) {
  const styles = useStyles();
  const c = theme.colors;
  const pinstripe = theme.titleBar.style === 'pinstripe';

  return (
    <View style={[styles.swatch, { backgroundColor: c.desktop }]}>
      <View style={[styles.swatchWindow, { backgroundColor: c.face, borderColor: c.darkShadow }]}>
        <View style={[styles.swatchTitle, { backgroundColor: c.titleActiveLeft }]}>
          {pinstripe ? (
            // Two rules stand in for Platinum's full set at this size.
            <>
              <View style={[styles.swatchStripe, { backgroundColor: c.titleActiveRight, top: 1 }]} />
              <View style={[styles.swatchStripe, { backgroundColor: c.titleActiveRight, top: 3 }]} />
            </>
          ) : (
            <View style={[styles.swatchFade, { backgroundColor: c.titleActiveRight }]} />
          )}
        </View>
        <View style={styles.swatchRow}>
          <View style={[styles.swatchField, { backgroundColor: c.fieldBg, borderColor: c.shadow }]} />
          <View style={[styles.swatchButton, { backgroundColor: c.face, borderColor: c.highlight }]} />
        </View>
        <View style={[styles.swatchAccent, { backgroundColor: c.accent }]} />
      </View>
    </View>
  );
}

const useStyles = makeStyles((t) => ({
  backdrop: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 12 },
  window: { width: '100%', maxWidth: 380, flex: isDesktop ? 0 : 1, maxHeight: 560 },
  frameless: { flex: 1, backgroundColor: t.colors.face, padding: 3 },
  body: { padding: 10, gap: 6 },
  hint: { fontSize: 10, marginBottom: 4 },
  section: { marginTop: 8 },

  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  cardHit: { flexGrow: 1, flexBasis: 104, minWidth: 104 },
  card: { flex: 1 },
  cardInner: { padding: 5, gap: 4 },
  cardLabel: { fontSize: t.fontSizes.ui },
  cardDesc: { fontSize: 9, lineHeight: 12 },

  swatch: { height: 54, padding: 5, justifyContent: 'center' },
  swatchWindow: { flex: 1, borderWidth: 1, padding: 2, gap: 2 },
  swatchTitle: { height: 7, justifyContent: 'center' },
  swatchFade: { position: 'absolute', right: 0, top: 0, bottom: 0, width: '45%' },
  swatchStripe: { position: 'absolute', left: 0, right: 0, height: 1 },
  swatchRow: { flexDirection: 'row', gap: 2, flex: 1 },
  swatchField: { flex: 2, borderWidth: 1 },
  swatchButton: { flex: 1, borderWidth: 1 },
  swatchAccent: { height: 3 },

  wallSwatch: { height: 40, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' },
  wallPlus: { fontSize: 18 },
  error: { color: '#800000', fontSize: 10 },

  wellInner: { padding: 5 },
  actions: { flexDirection: 'row', marginTop: 8 },
  action: { flex: 1 },
}));
